import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Loader } from 'lucide-react'
import type { ImageDTO } from '@shared/types'
import { ImageStatus, effectiveAlt, hasOriginalAlt } from '@shared/types'
import type { TextareaTone } from '@/components/ui'
import { Skeleton, Textarea, useToast } from '@/components/ui'
import { errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useMediaStore } from '@/stores/useMediaStore'

/** Long enough that a sentence is one request, short enough to feel automatic. */
const PATCH_DEBOUNCE_MS = 600
const SAVED_HINT_MS = 1800

/** `CLAUDE_ALT_PROMPT` asks for "under 125 characters", so that is the soft target. */
const ALT_TARGET = 125

export interface AltTextEditorProps {
  image: ImageDTO
}

type Indicator = 'idle' | 'saving' | 'saved'

/**
 * Tint carries state, so a wall of cards is scannable without reading badges:
 * red failed, grey already-in-WordPress, violet approved, green freshly
 * generated, amber alt text WordPress already had.
 */
function toneFor(image: ImageDTO, dirty: boolean): TextareaTone {
  if (image.status === ImageStatus.Failed || image.error !== null) return 'danger'
  if (image.saved && !dirty) return 'muted'
  if (image.approved) return 'accent'
  if (image.generatedAlt !== null || image.editedAlt !== null) return 'success'
  if (hasOriginalAlt(image)) return 'warning'
  return 'default'
}

export function AltTextEditor({ image }: AltTextEditorProps): ReactElement {
  const patchImage = useMediaStore((state) => state.patchImage)
  const { toast } = useToast()

  const external = effectiveAlt(image)
  const [value, setValue] = useState(external)
  const [indicator, setIndicator] = useState<Indicator>('idle')

  const externalRef = useRef(external)
  const valueRef = useRef(value)
  const timerRef = useRef<number | null>(null)
  /** True from the first keystroke until the server has acknowledged it. */
  const dirtyRef = useRef(false)
  const imageIdRef = useRef(image.id)

  // A server-side change (a generated result landing, an approval from another
  // tab) syncs in — unless the user has an unsaved edit, which never gets
  // clobbered by a snapshot that predates it.
  useEffect(() => {
    if (external === externalRef.current) return
    externalRef.current = external
    if (dirtyRef.current) return
    setValue(external)
  }, [external])

  useEffect(() => {
    if (indicator !== 'saved') return
    const timer = window.setTimeout(() => setIndicator('idle'), SAVED_HINT_MS)
    return () => window.clearTimeout(timer)
  }, [indicator])

  const flush = useCallback(
    async (next: string): Promise<void> => {
      setIndicator('saving')
      try {
        await patchImage(imageIdRef.current, { editedAlt: next })
        dirtyRef.current = false
        externalRef.current = next
        setIndicator('saved')
      } catch (error) {
        dirtyRef.current = false
        setIndicator('idle')
        // The store already rolled the row back; mirror it so the field shows
        // what the server actually holds rather than a phantom edit.
        const current = useMediaStore.getState().items.find((item) => item.id === imageIdRef.current)
        if (current) {
          externalRef.current = effectiveAlt(current)
          setValue(externalRef.current)
        }
        toast({
          title: 'Could not save that alt text',
          description: errorMessage(error),
          variant: 'error'
        })
      }
    },
    [patchImage, toast]
  )

  useEffect(
    () => () => {
      if (timerRef.current === null) return
      window.clearTimeout(timerRef.current)
      // Navigating away must not drop the last keystrokes. This component is
      // gone, so the patch goes through the store with no state to update.
      void useMediaStore
        .getState()
        .patchImage(imageIdRef.current, { editedAlt: valueRef.current })
        .catch(() => {})
    },
    []
  )

  const handleChange = (next: string): void => {
    setValue(next)
    valueRef.current = next
    dirtyRef.current = true

    // Editing invalidates approval. The debounced text patch clears it too, but
    // sending it now makes the badge and the tint react on the first keystroke.
    if (image.approved) {
      void patchImage(image.id, { approved: false }).catch(() => {
        // The text patch that follows surfaces anything genuinely broken.
      })
    }

    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void flush(next)
    }, PATCH_DEBOUNCE_MS)
  }

  const handleBlur = (): void => {
    if (timerRef.current === null) return
    window.clearTimeout(timerRef.current)
    timerRef.current = null
    void flush(valueRef.current)
  }

  if (image.status === ImageStatus.Downloading || image.status === ImageStatus.Processing) {
    return (
      <div className="space-y-2 py-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-11/12" />
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    )
  }

  const label = image.title.trim() || `attachment ${image.wpMediaId}`
  const length = value.length
  const over = length > ALT_TARGET
  const countId = `alt-count-${image.id}`

  return (
    <div className="space-y-1.5">
      <Textarea
        aria-label={`Alt text for ${label}`}
        aria-describedby={countId}
        tone={toneFor(image, dirtyRef.current)}
        rows={3}
        value={value}
        placeholder="No alt text yet — generate it, or write your own."
        onChange={(event) => handleChange(event.target.value)}
        onBlur={handleBlur}
        className="min-h-18 text-[0.8125rem]"
      />

      <div className="flex items-center justify-between gap-2 px-0.5">
        <span className="inline-flex h-4 items-center gap-1 text-[11px] font-medium text-fg-subtle">
          {indicator === 'saving' ? (
            <>
              <Loader className="size-3 animate-spin" aria-hidden="true" />
              Saving…
            </>
          ) : null}
          {indicator === 'saved' ? (
            <>
              <Check className="size-3" aria-hidden="true" />
              Edit saved
            </>
          ) : null}
        </span>
        <span
          id={countId}
          title={`Aim for under ${ALT_TARGET} characters`}
          className={cn(
            'shrink-0 text-[11px] tabular-nums',
            over ? 'font-medium text-warn-700 dark:text-warn-300' : 'text-fg-subtle'
          )}
        >
          {length}/{ALT_TARGET}
        </span>
      </div>
    </div>
  )
}
